import type { RefObject } from "react";

import { useEffect, useRef } from "react";

import { useEventCallback } from "@alaffia-technology-solutions/hooks";

import { useShallow } from "zustand/react/shallow";

import { usePDFStore } from "./providers";

interface UsePDFViewerSyncOptions {
  ref: RefObject<HTMLElement | null>;
  url: string;
  fileId: string;
  pageIndex?: number;
}

export function usePDFViewerSync({
  ref,
  url,
  fileId,
  pageIndex,
}: UsePDFViewerSyncOptions) {
  const { data: pdfViewerCtrl, isServer } = usePDFStore(
    useShallow((store) => ({
      state: store.state,
      init: store.init,
      open: store.open,
      close: store.close,
      pdfViewer: store.pdfViewer,
      jumpToPage: store.jumpToPage,
    })),
  );

  const { init, open, close, state, jumpToPage } = pdfViewerCtrl ?? {};

  const initCallback = useEventCallback(init);
  const openCallback = useEventCallback(open);
  const closeCallback = useEventCallback(close);
  const jumpToPageCallback = useEventCallback(jumpToPage);

  const stateRef = useRef(state);

  useEffect(() => {
    stateRef.current = state;
  });

  useEffect(() => {
    if (isServer) {
      return;
    }
    const el = ref.current;
    if (!el) {
      return;
    }
    let runningCleanup = false;
    initCallback(el);
    const runOpen = async () => {
      // Await empty promise to prevent opening PDF viewer twice in react strict mode
      await Promise.resolve();
      if (runningCleanup) {
        return;
      }
      return openCallback(url, fileId)?.catch(console.error);
    };
    void runOpen();

    return () => {
      runningCleanup = true;
      closeCallback();
    };
  }, [isServer, initCallback, openCallback, closeCallback, url, fileId, ref]);

  useEffect(() => {
    if (isServer || stateRef.current !== "opened" || pageIndex === undefined) {
      return;
    }

    void jumpToPageCallback(pageIndex)?.catch(console.error);
  }, [isServer, jumpToPageCallback, pageIndex]);
}
